import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { Card, Row, Col, Button, Badge } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Empty,
  Flex,
  Spin,
  Tag,
  Modal,
  Form,
  Input,
  message,
  Timeline,
  Upload,
  Image,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  UploadOutlined,
  DownloadOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import workExecutionApis from "../../api/WorkOrderManagement/WorkExecutionApis";

const { Panel } = AntCollapse;
const { TextArea } = Input;

interface WorkLine {
  id: string;
  workLineNumber: string;
  workLineTitle: string;
  description: string;
  approvedQuantity: number;
  executedQuantity: number;
  cumulativeExecuted: number;
  percentCompletion: number;
  remainingQuantity: number;
  remarks: string;
  uom: string;
}

const WorkExecutionBatchDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [batch, setBatch] = useState<any>(null);
  const [workOrder, setWorkOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [sm, setSm] = useState(false);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "return" | null>(
    null
  );
  const [form] = Form.useForm();
  const [workLines, setWorkLines] = useState<WorkLine[]>([]);

  useEffect(() => {
    console.log("batchId", id);
    if (id) {
      loadBatchData(id);
    }
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, [id]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const mapApiBatchDataToComponent = (apiData: any) => {
    return {
      batchId: apiData.workExeBatchId,
      batchNumber: apiData.workBatchSequenceId,
      woNumber: apiData.workOrderNumber,
      woId: apiData.workOrderId,
      workOrderTitle: apiData.workOrderTitle,
      siteName: apiData.siteName,
      orgName: apiData.orgName,
      projectName: apiData.projectName,
      status: apiData.status,
      submittedOn: apiData.submittedAt,
      submittedBy: apiData.createdByName,
      totalExecutedQuantity: apiData.totalExecutedQuantity,
      totalLines: apiData.batchLines?.length || 0,
      slaAge: Math.ceil(
        (new Date().getTime() - new Date(apiData.submittedAt).getTime()) /
          (1000 * 3600 * 24)
      ),
      batchLines: apiData.batchLines || [],
      dms: apiData.dms || [],
      attachments: apiData.attachments || [],
      paginationId: apiData.paginationId,
    };
  };

  const mapBatchLinesToWorkLines = (batchLines: any[]): WorkLine[] => {
    if (!batchLines || batchLines.length === 0) {
      return [];
    }

    return batchLines.map((line, index) => {
      const approvedQty = line.approvedQuantity || 0;
      const executedQty = line.executedQuantity || 0;
      const cumulativeQty = line.cumulativeExecutedQuantity || 0;

      // Calculate percentage completion
      const percentCompletion =
        approvedQty > 0
          ? Math.round(
              ((cumulativeQty + executedQty) / approvedQty) * 100 * 100
            ) / 100
          : 0;

      // Calculate remaining quantity
      const remainingQuantity = Math.max(
        0,
        approvedQty - cumulativeQty - executedQty
      );

      return {
        id: line.executionBatchLineId?.toString() || index.toString(),
        workLineNumber: `WOL-${String(line.workOrderLineId).padStart(3, "0")}`,
        workLineTitle: line.taskTitle || `Task ${index + 1}`,
        description: line.taskTitle || "No description available",
        approvedQuantity: approvedQty,
        executedQuantity: executedQty,
        cumulativeExecuted: cumulativeQty,
        percentCompletion: percentCompletion,
        remainingQuantity: remainingQuantity,
        remarks: "", // Not provided in API response
        uom: "Units", // Default UOM as not provided
      };
    });
  };

  const loadBatchData = async (batchId: string) => {
    try {
      setLoading(true);
      if (!batchId) {
        message.error("Batch ID is required");
        setLoading(false);
        return;
      }

      const response: any = await workExecutionApis.workExecutionById(batchId);

      if (response && response.status && response.data) {
        const mappedBatch = mapApiBatchDataToComponent(response.data);
        setBatch(mappedBatch);

        // Map batch lines to work lines
        const workLines = mapBatchLinesToWorkLines(response.data.batchLines);
        setWorkLines(workLines);

        // Set work order data from batch response
        setWorkOrder({
          id: mappedBatch.woId,
          woNumber: mappedBatch.woNumber,
          title: mappedBatch.workOrderTitle,
          site: mappedBatch.siteName,
          project: mappedBatch.projectName,
          organisation: mappedBatch.orgName,
        });
      } else {
        throw new Error(
          response?.message || "Failed to fetch execution batch details"
        );
      }
    } catch (error) {
      console.error("Error loading batch:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load execution batch data",
      });
    } finally {
      setLoading(false);
    }
  };

  const openActionModal = (type: "approve" | "return") => {
    if (type === "approve") {
      // Show SweetAlert confirmation dialog for approval
      Swal.fire({
        title: 'Confirm Approval',
        text: 'Are you sure you want to approve this execution batch?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, Approve',
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          handleApproval();
        }
      });
    } else {
      // Show modal for rejection with reason input
      setActionType(type);
      setActionModalVisible(true);
      form.resetFields();
    }
  };

  const handleApproval = async () => {
    try {
      const response: any = await workExecutionApis.approveWorkExecution({
        workOrderId: batch.woId,
        workExeBatchId: id
      });

      if (response && response.status) {
        message.success("Execution batch approved successfully!");
        setTimeout(() => {
          navigate(`${process.env.PUBLIC_URL}/work-execution-pending-approvals`);
        }, 1500);
      } else {
        throw new Error(response?.message || 'Failed to approve execution batch');
      }
    } catch (error) {
      console.error("Error approving batch:", error);
      message.error("Failed to approve execution batch. Please try again.");
    }
  };

  const handleActionSubmit = async (values: any) => {
    try {
      console.log("Submitting action:", actionType, values);

      if (actionType === "return") {
        // Call reject API with proper request format
        const response: any = await workExecutionApis.rejectWorkExecution({
          workExeBatchId: batch.batchId,
          rejectionComments: values.reason
        });

        if (response && response.status) {
          message.success("Execution batch rejected successfully!");
          setActionModalVisible(false);
          form.resetFields();
          
          setTimeout(() => {
            navigate(`${process.env.PUBLIC_URL}/work-execution-pending-approvals`);
          }, 1500);
        } else {
          throw new Error(response?.message || 'Failed to reject execution batch');
        }
      }
    } catch (error) {
      console.error("Error submitting action:", error);
      message.error("Failed to reject execution batch. Please try again.");
    }
  };

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Submitted: "processing",
      Pending: "warning",
      Checked: "warning",
      Verified: "success",
      Approved: "success",
      Rejected: "error",
      Returned: "error",
    };
    return statusColors[status] || "default";
  };

  // Execution Lines Table Columns for Review
  const executionLinesColumns: TableColumnsType<WorkLine> = [
    {
      title: "Work Line #",
      dataIndex: "workLineNumber",
      key: "workLineNumber",
      width: 120,
      fixed: "left",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Work Line Title",
      dataIndex: "workLineTitle",
      key: "workLineTitle",
      width: 200,
      render: (text, record) => (
        <div>
          <div className="fw-bold">{text}</div>
          <div className="text-muted small">{record.description}</div>
        </div>
      ),
    },
    {
      title: "Approved Qty",
      dataIndex: "approvedQuantity",
      key: "approvedQuantity",
      width: 120,
      align: "right",
      render: (qty, record) => (
        <div>
          <div className="fw-bold">{qty.toLocaleString()}</div>
          <div className="text-muted small">{record.uom}</div>
        </div>
      ),
    },
    {
      title: "Executed Qty (Submitted)",
      dataIndex: "executedQuantity",
      key: "executedQuantity",
      width: 150,
      align: "right",
      render: (qty, record) => (
        <div>
          <div className="fw-bold text-primary">{qty.toLocaleString()}</div>
          <div className="text-muted small">{record.uom}</div>
        </div>
      ),
    },
    {
      title: "Cumulative (After Approval)",
      key: "cumulativeAfter",
      width: 150,
      align: "right",
      render: (_, record) => (
        <div>
          <div className="fw-bold text-success">
            {(
              record.cumulativeExecuted
            ).toLocaleString()}
          </div>
          <div className="text-muted small">{record.uom}</div>
        </div>
      ),
    },
    {
      title: "% Completion (Post Approval)",
      key: "percentCompletion",
      width: 160,
      align: "center",
      render: (_, record) => {
        const percent =
          Math.round(
            ((record.cumulativeExecuted + record.executedQuantity) /
              record.approvedQuantity) *
              100 *
              100
          ) / 100;
        const color =
          percent === 100
            ? "#52c41a"
            : percent >= 75
            ? "#faad14"
            : percent >= 50
            ? "#1890ff"
            : "#ff4d4f";
        return (
          <Tag color={color} style={{ minWidth: 60, textAlign: "center" }}>
            {percent}%
          </Tag>
        );
      },
    },
    {
      title: "Remaining (Post Approval)",
      key: "remainingQuantity",
      width: 150,
      align: "right",
      render: (_, record) => (
        <div>
          <div className="fw-bold">
            {Math.max(
              0,
              record.approvedQuantity -
                record.cumulativeExecuted -
                record.executedQuantity
            ).toLocaleString()}
          </div>
          <div className="text-muted small">{record.uom}</div>
        </div>
      ),
    },
    {
      title: "Contractor Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 200,
      render: (text) => text || <span className="text-muted">---</span>,
    },
  ];

  if (loading) {
    return (
      <>
        <Head title="Execution Batch Details" />
        <Content>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </Content>
      </>
    );
  }

  if (!batch) {
    return (
      <>
        <Head title="Execution Batch Not Found" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Execution batch not found" />
          </div>
        </Content>
      </>
    );
  }

  const getSlaColor = (age: number): string => {
    if (age <= 1) return "success";
    if (age <= 2) return "warning";
    return "danger";
  };

  const canApprove =
    batch?.status === "Submitted" ||
    batch?.status === "Pending" ||
    batch?.status === "Checked" ||
    batch?.status === "Verified";

  return (
    <>
      <Head title={`Execution Batch - ${batch.batchNumber}`} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">
                      <span>
                        {batch.batchNumber} | {batch.status}
                      </span>
                      {batch.slaAge > 2 && (
                        <Badge color="danger" className="ml-2">
                          SLA Breach
                        </Badge>
                      )}
                    </BlockTitle>
                  </BlockHeadContent>
                  <div className="d-flex">
                    {/* Action buttons for approval */}
                    {canApprove && (
                      <div className="actions clearfix">
                        <ul
                          className="m-auto d-flex align-items-center justify-content-center p-0"
                          style={{ flexDirection: "row-reverse" }}
                        >
                          <li className="pt-0 pb-0">
                            <Button
                              color="success"
                              onClick={() => openActionModal("approve")}
                            >
                              {/* <CheckCircleOutlined /> */}
                              Approve
                            </Button>
                          </li>
                          <li className="pt-0 pb-0">
                            <Button
                              color="warning"
                              onClick={() => openActionModal("return")}
                            >
                              {/* <CloseCircleOutlined /> */}
                              Reject
                            </Button>
                          </li>
                        </ul>
                      </div>
                    )}

                    <div className="actions clearfix">
                      <ul
                        className="m-auto d-flex align-items-center justify-content-center p-0"
                        style={{ flexDirection: "row-reverse" }}
                      >
                        <li className="pt-0 pb-0">
                          <Button
                            className="toggle"
                            color="primary"
                            onClick={() => {
                              navigate(
                                `${process.env.PUBLIC_URL}/work-execution-pending-approvals`
                              );
                            }}
                          >
                            <Icon name="arrow-left" />
                            <span>Back</span>
                          </Button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${
                        sm ? "active" : ""
                      }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              <div className="content clearfix">
                <AntCollapse defaultActiveKey={["1", "2", "3"]}>
                  <Panel header="Batch Information" key="1">
                    <Col className="justify-start w-100 flex-wrap  p-3">
                      <div className="w-30 border-end">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Batch #</div>
                          <div className="data-value w-70">
                            {batch.batchNumber || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">WO #</div>
                          <div className="data-value w-70">
                            {batch.woNumber || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Site</div>
                          <div className="data-value w-70">
                            {batch.siteName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Project</div>
                          <div className="data-value w-70">
                            {batch.projectName || "--"}
                          </div>
                        </div>
                      </div>
                      <div className="w-30 border-end paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Status</div>
                          <div className="data-value w-70">
                            <Badge
                              color={`outline-${getStatusColor(batch.status)}`}
                            >
                              {batch.status}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Organization</div>
                          <div className="data-value w-70">
                            <Badge color="outline-info">
                              {batch.orgName || "Not specified"}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">SLA Age</div>
                          <div className="data-value w-70">
                            <Badge
                              color={`outline-${getSlaColor(batch.slaAge)}`}
                            >
                              {batch.slaAge} days
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Total Lines</div>
                          <div className="data-value w-70">
                            <Badge color="outline-primary">
                              {batch.totalLines}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="w-40 paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Submitted On</div>
                          <div className="data-value w-70">
                            {dayjs(batch.submittedOn).format("DD-MMM-YYYY")}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Submitted By</div>
                          <div className="data-value w-70">
                            {batch.submittedBy || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">
                            Work Order Title
                          </div>
                          <div className="data-value w-80">
                            {batch.workOrderTitle
                              ? batch.workOrderTitle.length > 40
                                ? `${batch.workOrderTitle.substring(0, 40)}...`
                                : batch.workOrderTitle
                              : "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">
                            Total Executed Qty
                          </div>
                          <div className="data-value w-70">
                            <Badge color="outline-success">
                              {batch.totalExecutedQuantity?.toLocaleString()}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Panel>

                  <Panel
                    header={`Execution Lines (${workLines.length})`}
                    key="2"
                  >
                    <div className="table-responsive poTable">
                      <div
                        style={{
                          width: screenSize - 170 + "px",
                          margin: "auto",
                        }}
                      >
                        <Table
                          className="customTable"
                          columns={executionLinesColumns}
                          dataSource={workLines}
                          scroll={{ x: 1800 }}
                          bordered
                          size="small"
                          pagination={false}
                          rowKey="id"
                        />
                      </div>
                    </div>
                  </Panel>

                  <Panel header="Evidence & Attachments" key="3">
                    {batch.attachments && batch.attachments.length > 0 ? (
                      <div className="mt-3">
                        <div className="d-flex flex-wrap">
                          {batch.attachments.map(
                            (attachment: any, index: number) => (
                              <div key={index} className="me-3 mb-2">
                                <Badge color="outline-info">
                                  <PaperClipOutlined /> {attachment.fileName}
                                  <Button
                                    size="sm"
                                    color="link"
                                    className="p-0 ms-2"
                                  >
                                    <DownloadOutlined />
                                  </Button>
                                </Badge>
                              </div>
                            )
                          )}
                        </div>
                        {batch.notes && (
                          <div className="mt-3">
                            <h6>
                              <strong>Notes:</strong>
                            </h6>
                            <p>{batch.notes}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-3">
                        <Empty description="No attachments uploaded" />
                      </div>
                    )}
                  </Panel>

                  {/* <Panel header="Action History" key="4">
                    <Timeline>
                      <Timeline.Item color="blue">
                        <p>
                          <strong>Batch Submitted</strong>
                        </p>
                        <p className="text-muted">
                          {dayjs(batch.submittedOn).format("DD MMM YYYY HH:mm")}{" "}
                          by {batch.submittedBy}
                        </p>
                      </Timeline.Item>

                      {batch.lastActionBy && (
                        <Timeline.Item color="green">
                          <p>
                            <strong>Last Action</strong>
                          </p>
                          <p className="text-muted">
                            {dayjs(batch.lastActionDate).format(
                              "DD MMM YYYY HH:mm"
                            )}{" "}
                            by {batch.lastActionBy}
                          </p>
                        </Timeline.Item>
                      )}
                    </Timeline>
                  </Panel> */}
                </AntCollapse>
              </div>
            </div>
          </div>
        </Card>
      </Content>

      {/* Rejection Modal */}
      <Modal
        title="Reject Execution Batch"
        open={actionModalVisible}
        onCancel={() => {
          setActionModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleActionSubmit}>
          <Form.Item
            name="reason"
            label="Reason for Rejection"
            rules={[
              {
                required: true,
                message: "Please provide reason for rejecting the batch",
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Please explain why you are rejecting this execution batch..."
            />
          </Form.Item>

          <div className="text-right">
            <Button
              className="me-2"
              onClick={() => setActionModalVisible(false)}
            >
              Cancel
            </Button>
            <Button color="danger" type="submit">
              Reject Batch
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default WorkExecutionBatchDetails;
